import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Auth } from '@barracuda/shared';
import { useAppContext } from '@/context/App.context';
import { useLocalizedNavigate } from '@/hooks/useLocalizedNavigate';
import { useLocation } from 'react-router-dom';
import { AxiosResponse } from 'axios';
import { Box, Button, Grid, MenuItem, Select, Skeleton, Stack, Typography } from '@mui/material';
import { theme } from '@theme';
import { useTranslation } from 'react-i18next';
import { HelpCenterCategoryItem } from './HelpCenterCategoryItem';
import { HelpCenterQuestionItem } from './HelpCenterQuestionItem';
import useCheckBreakpoint from 'src/hooks/useCheckBreakpoint';
import headerIllustration from '@images/helpCenter__header.svg';
import caretDownIllustration from '@images/caret-down.svg';
import chatIllustration from '@images/helpCenter__chat.svg';
import ticketIllustration from '@images/helpCenter__ticket.svg';
import * as styles from './HelpCenter.styles';
import Img from '@barracuda/shared/components/Primitive/Img';

export interface HelpCenterQuestion {
  questionId: number;
  questionLabel: string;
  text: string;
}

export interface HelpCenterCategory {
  id: number;
  label: string;
  questions: HelpCenterQuestion[];
}

const useQueryParams = (): { questionId: number | null; categoryId: number | null } => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const questionId = searchParams.get('questionId');
  const categoryId = searchParams.get('categoryId');
  return {
    questionId: questionId ? Number(questionId) : null,
    categoryId: categoryId ? Number(categoryId) : null
  };
};

export const HelpCenter: FC = () => {
  const { language } = useAppContext();
  const navigate = useLocalizedNavigate();
  const { categoryId, questionId } = useQueryParams();
  const [activeCategoryId, setActiveCategoryId] = useState<number | null>(null);
  const [expandedQuestionId, setExpandedQuestionId] = useState<number | null>(null);
  const [categories, setCategories] = useState<HelpCenterCategory[]>([]);
  const { t } = useTranslation();
  const { breakpointDown: isMobile, breakpointUp: isTablet } = useCheckBreakpoint('tablet');

  useEffect(() => {
    Auth.sopClient
      .getHelpCenterCategories(language || 'ENG')
      .then((result: AxiosResponse<HelpCenterCategory[]>) => {
        const fetchedCategories = result.data;
        setCategories(fetchedCategories);
        setActiveCategoryId(fetchedCategories?.[0]?.id);
      });
  }, [language]);

  useEffect(() => {
    if (!categories.length || !questionId || !categoryId) {
      return;
    }

    setExpandedQuestionId(questionId);
    setActiveCategoryId(categoryId);

    // Wait for DOM to update after state changes
    setTimeout(() => {
      scrollToQuestion();
    }, 150);
  }, [categories, questionId, categoryId]);

  const currentCategoryLabel = useCallback(() => {
    return categories.find((category) => {
      return category.id === activeCategoryId;
    })?.label;
  }, [activeCategoryId]);

  const scrollToQuestion = () => {
    if (!questionId) return;

    const element = document.getElementById(`question-item-${categoryId}-${questionId}`);
    if (element) {
      const topPosition = element.getBoundingClientRect().top + window.scrollY - 90;
      window.scrollTo({
        top: topPosition,
        behavior: 'smooth'
      });
    }
  };

  const clearSearch = () => {
    const searchParams = new URL(location.href).searchParams;
    searchParams.delete('categoryId');
    searchParams.delete('questionId');
    navigate({ search: searchParams.toString() });
  };

  const onChangeCategory = useCallback((categoryId: number) => {
    setActiveCategoryId(categoryId);
    setExpandedQuestionId(null);
    clearSearch();
  }, []);

  const onQuestionClick = useCallback(
    (questionId: number) => {
      setExpandedQuestionId(expandedQuestionId === questionId ? null : questionId);
      clearSearch();
    },
    [expandedQuestionId]
  );

  const categoryItems = useMemo(() => {
    if (!categories.length) {
      return <Skeleton animation='wave' variant='rectangular' height={400} />;
    }
    return categories.map((category, index) => (
      <HelpCenterCategoryItem
        onClick={() => onChangeCategory(category.id)}
        active={activeCategoryId === category.id}
        label={category.label}
        key={`category-item-${index}`}
      />
    ));
  }, [categories, activeCategoryId]);

  const activeQuestions = useMemo(() => {
    return categories.find((category) => category.id === activeCategoryId)?.questions || [];
  }, [categories, activeCategoryId]);

  const QuestionsSkeleton = useCallback(
    () => (
      <Box>
        {[1, 2, 3, 4].map((index) => (
          <Skeleton
            key={`question-skeleton-${index}`}
            sx={{ mb: '10px' }}
            animation='wave'
            variant='rectangular'
            height={68}
          />
        ))}
      </Box>
    ),
    []
  );

  const CaretDownIcon = () => (
    <Img sx={styles.helpCenterCategoriesCaretDown} src={caretDownIllustration} />
  );

  const onChatClick = () => {
    window.open(process.env.REACT_APP_SUPPORT_LINK, '_blank', 'noopener,noreferrer');
  };

  const onSupportClick = () => {
    window.open(process.env.REACT_APP_SUPPORT_FORM_LINK, '_blank', 'noopener,noreferrer');
  };

  const ctaSection = useMemo(
    () => (
      <Stack sx={styles.helpCenterCtaWrapper}>
        <Img src={chatIllustration} alt='Chat Illustration' />
        <Typography sx={styles.helpCenterCtaHeader} component='h3'>
          {t('helpCenterPage.chatWithUs')}
        </Typography>
        <Typography sx={styles.helpCenterCtaText}>{t('helpCenterPage.chatDescription')}</Typography>
        <Button size='large' sx={styles.helpCenterCtaButton} onClick={onChatClick}>
          {t('helpCenterPage.openChat')}
        </Button>

        <Img src={ticketIllustration} sx={{ marginTop: '64px' }} alt='Ticket Illustration' />
        <Typography sx={styles.helpCenterCtaHeader} component='h3'>
          {t('helpCenterPage.createTicket')}
        </Typography>
        <Typography sx={styles.helpCenterCtaText}>
          {t('helpCenterPage.ticketDescription')}
        </Typography>
        <Button size='large' sx={styles.helpCenterCtaButton} onClick={onSupportClick}>
          {t('helpCenterPage.goToForm')}
        </Button>
      </Stack>
    ),
    [t]
  );

  return (
    <Box sx={{ backgroundColor: theme.palette.common.lightGrey5 }}>
      <Box sx={{ backgroundColor: theme.palette.common.feedbackInfo10 }}>
        <Stack sx={styles.helpCenterHeader}>
          <Stack justifyContent='center'>
            <Typography component='h2' sx={styles.helpCenterHeaderTitle}>
              {t('helpCenterPage.header')}
            </Typography>
            <Typography sx={styles.helpCenterHeaderText}>
              {t('helpCenterPage.headerText')}
            </Typography>
          </Stack>
          {isTablet && <Img src={headerIllustration} alt='Help Center Illustration' />}
        </Stack>
      </Box>
      <Box sx={styles.helpCenterContent}>
        <Grid container sx={{ gap: '32px' }}>
          <Grid item mobile={12} tablet={4} component='aside'>
            <Box sx={styles.helpCenterSidebarBox}>
              <Box sx={styles.helpCenterFadeInAnimation}>
                <Typography sx={styles.helpCenterCategoriesHeader}>
                  {t('helpCenterPage.categories')}
                </Typography>
                {isMobile && (
                  <Select
                    value={activeCategoryId}
                    onChange={(event) => onChangeCategory(Number(event.target.value))}
                    IconComponent={CaretDownIcon}
                    sx={styles.helpCenterMobileCategories}
                  >
                    {categories.map((category, index) => (
                      <MenuItem key={'selectCategory' + index} value={category.id}>
                        {category.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                {isTablet && categoryItems}
              </Box>
            </Box>
            {isTablet && ctaSection}
          </Grid>
          <Grid item mobile={12} tablet={7} sx={styles.helpCenterArticlesContainer}>
            <Box key={activeCategoryId} sx={styles.helpCenterFadeInAnimation}>
              <Typography sx={styles.helpCenterCategoryTitle} component='h2'>
                {currentCategoryLabel()}
              </Typography>
              {!categories.length && <QuestionsSkeleton />}
              {activeQuestions.map((question) => (
                <HelpCenterQuestionItem
                  question={question}
                  onClick={() => onQuestionClick(question.questionId)}
                  key={`question-item-${activeCategoryId}-${question.questionId}`}
                  expanded={expandedQuestionId === question.questionId}
                  categoryId={activeCategoryId || 0}
                />
              ))}
            </Box>
          </Grid>
          <Grid mobile={12}>{isMobile && ctaSection}</Grid>
        </Grid>
      </Box>
    </Box>
  );
};
